import { montoALetras } from './numeroALetras';
import { EMPRESA_NOMBRE, EMPRESA_RUC, EMPRESA_DIRECCION } from './empresa';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const fmtFecha = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

const periodoFactura = (f: any) => (f?.mes && f?.anio) ? `${MESES[f.mes - 1]} ${f.anio}` : '';

const fila = (label: string, valor: string) => valor
  ? `<div class="row"><span class="lbl">${label}</span><span class="val">${valor}</span></div>`
  : '';

const filaTxt = (label: string, valor: string) => valor ? `${label} ${valor}\n` : '';

// Construye el documento HTML completo de un recibo detallado a partir de un Pago
// (con relaciones cliente/factura/cobrador cargadas). Usado por el modulo de Pagos
// (admin/gerente) y por el Portal del Cliente para que ambos muestren lo mismo.
export function construirReciboHtml(p: any, waUrl?: string): string {
  const monto = Number(p.monto);
  const periodo = periodoFactura(p.factura);
  const cliente = p.cliente || {};
  const cobrador = p.cobrador || {};

  return `<html><head><title>Recibo de Pago</title><style>
    *{box-sizing:border-box}
    body{font-family:Arial,Helvetica,sans-serif;padding:16px;max-width:300px;margin:0 auto;background:#fff;color:#111}
    .logo{text-align:center;font-size:30px;margin-bottom:2px}
    h2{text-align:center;font-size:15px;margin:0 0 2px;font-weight:bold}
    .sub{text-align:center;font-size:11px;color:#555}
    .empresa-info{text-align:center;font-size:10px;color:#666;margin-bottom:10px;line-height:1.4}
    hr{border:none;border-top:1px solid #999;margin:8px 0}
    .titulo-recibo{text-align:center;border-top:1px solid #111;border-bottom:1px solid #111;padding:6px 0;margin:8px 0}
    .titulo-recibo b{font-size:13px}
    .periodo-mes{font-size:11px;font-weight:bold;margin-top:2px}
    .row{display:flex;justify-content:space-between;margin-bottom:4px;font-size:11px;gap:8px}
    .lbl{color:#666;white-space:nowrap}.val{font-weight:bold;text-align:right}
    .seccion{font-size:10px;font-weight:bold;color:#666;margin:8px 0 4px;text-align:left}
    .total-row{display:flex;justify-content:space-between;align-items:baseline;margin:8px 0;border-top:1px solid #111;border-bottom:1px solid #111;padding:6px 0}
    .total-lbl{font-size:12px;font-weight:bold}
    .total-val{font-size:15px;font-weight:bold}
    .letras{text-align:center;font-size:10px;font-style:italic;margin:8px 0}
    .footer{text-align:center;font-size:10px;color:#999;margin-top:8px}
    .condiciones{text-align:center;font-size:9px;color:#888;margin-top:8px;line-height:1.4}
    .wa-btn{display:block;margin:14px auto 0;background:#25D366;color:white;border:none;border-radius:8px;padding:10px 20px;font-size:13px;font-weight:bold;cursor:pointer;text-align:center;text-decoration:none;width:100%}
    @media print{.wa-btn{display:none}}
  </style></head><body>
  <div class="logo">💧</div>
  <h2>${EMPRESA_NOMBRE.toUpperCase()}</h2>
  <div class="empresa-info">RUC: ${EMPRESA_RUC}<br>${EMPRESA_DIRECCION}${cobrador.telefono ? `<br>Celular: ${cobrador.telefono}` : ''}</div>
  <div class="titulo-recibo">
    <b>RECIBO DE PAGO</b><br>
    <span style="font-size:11px">N° ${p.factura?.numero || 'S/N'}</span>
    ${periodo ? `<div class="periodo-mes">RECIBO DE ${periodo.toUpperCase()}</div>` : ''}
  </div>
  ${fila('Fecha Vencimiento:', fmtFecha(p.factura?.fechaVencimiento))}
  ${fila('Fecha de Pago:', new Date(p.createdAt).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}
  <hr>
  ${fila('RUC o DNI:', cliente.dni || '—')}
  ${fila('Razón Social:', `${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim())}
  ${fila('Dirección:', cliente.direccion || '')}
  <hr>
  <div class="seccion">SERVICIO</div>
  ${fila(periodo ? 'Agua potable' : (cliente.paquete?.nombre || 'Servicio'), `S/. ${monto.toFixed(2)}`)}
  ${fila('Método:', (p.metodo || '').toUpperCase())}
  <hr>
  <div class="total-row">
    <span class="total-lbl">IMPORTE TOTAL:</span>
    <span class="total-val">S/. ${monto.toFixed(2)}</span>
  </div>
  <div class="letras">SON: ${montoALetras(monto)}</div>
  ${p.notas ? `<div class="row"><span class="lbl">Notas:</span><span class="val">${p.notas}</span></div>` : ''}
  <div class="footer">Representación impresa de Recibo</div>
  <div class="condiciones"><b>CONDICIONES DE SERVICIO</b><br>Al recibir el presente documento acepto los términos y condiciones del servicio.</div>
  ${waUrl ? `<a class="wa-btn" href="${waUrl}" target="_blank">📱 Compartir por WhatsApp</a>` : ''}
  ${waUrl ? `<script>window.onload=()=>window.print();<\/script>` : ''}
  </body></html>`;
}

// Misma estructura que construirReciboHtml pero en texto plano, para el mensaje
// que se envia por WhatsApp (wa.me solo acepta texto, no HTML).
export function construirReciboTexto(p: any): string {
  const monto = Number(p.monto);
  const periodo = periodoFactura(p.factura);
  const cliente = p.cliente || {};
  const cobrador = p.cobrador || {};
  const sep = '-----------------------------------';

  return `*${EMPRESA_NOMBRE.toUpperCase()}*\n` +
    `RUC: ${EMPRESA_RUC}\n` +
    `${EMPRESA_DIRECCION}\n` +
    (cobrador.telefono ? `Celular: ${cobrador.telefono}\n` : '') +
    `\n` +
    `*RECIBO DE PAGO*\n` +
    `N° ${p.factura?.numero || 'S/N'}\n` +
    (periodo ? `RECIBO DE ${periodo.toUpperCase()}\n` : '') +
    `\n` +
    filaTxt('Fecha Vencimiento:', fmtFecha(p.factura?.fechaVencimiento)) +
    filaTxt('Fecha de Pago:', new Date(p.createdAt).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })) +
    `${sep}\n` +
    filaTxt('RUC o DNI:', cliente.dni || '—') +
    filaTxt('Razón Social:', `${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim()) +
    filaTxt('Dirección:', cliente.direccion || '') +
    `${sep}\n` +
    `*SERVICIO*\n` +
    filaTxt(periodo ? 'Agua potable:' : `${cliente.paquete?.nombre || 'Servicio'}:`, `S/. ${monto.toFixed(2)}`) +
    filaTxt('Método:', (p.metodo || '').toUpperCase()) +
    `${sep}\n` +
    `*IMPORTE TOTAL: S/. ${monto.toFixed(2)}*\n` +
    `\n` +
    `SON: ${montoALetras(monto)}\n` +
    (p.notas ? `Notas: ${p.notas}\n` : '') +
    `\n` +
    `Representación impresa de Recibo\n` +
    `*CONDICIONES DE SERVICIO*\n` +
    `Al recibir el presente documento acepto los términos y condiciones del servicio.`;
}
